import { Dispatch, SetStateAction, useCallback } from 'react';
import { AlertType } from '../../components';
import { PAGINATION_PAGE_SIZE } from '../../constants';
import { useHandleApiResponse, useToast } from '../../hooks';
import { KnowledgeHubRecord, useCreateKnowledgeHubMutation } from '../../services';
import { KnowledgeHubRecordType } from '../../types';
import KnowledgeHubField from './KnowledgeHubField';

interface CreateKnowledgeHubItemProps {
  currPage: number;
  currPageSize: number;
  recordId: string;
  type: KnowledgeHubRecordType;
  value: string;
  setCurrPage: Dispatch<SetStateAction<number>>;
  setDraftRecords: Dispatch<SetStateAction<Pick<KnowledgeHubRecord, 'id' | 'content'>[]>>;
  setExistingRecords: Dispatch<SetStateAction<KnowledgeHubRecord[]>>;
  setValue: Dispatch<SetStateAction<string>>;
}

const CreateKnowledgeHubItem = ({
  currPage,
  currPageSize,
  recordId,
  type,
  value,
  setCurrPage,
  setDraftRecords,
  setExistingRecords,
  setValue,
}: CreateKnowledgeHubItemProps) => {
  const [createKnowledgeHub, { isLoading: isCreateLoading }] = useCreateKnowledgeHubMutation();

  const handleApiResponse = useHandleApiResponse();
  const { showToast } = useToast();

  // Deletes the draft record from the local state.
  const handleDeleteDraft = useCallback(
    () => setDraftRecords((prev) => prev.filter((record) => record.id !== recordId)),
    [recordId, setDraftRecords]
  );

  const onCreateSuccess = useCallback(
    (newRecord: KnowledgeHubRecord) => {
      handleDeleteDraft();
      if (currPage > 0) {
        // If the user is not on the first page, navigate to the first page, refetching the records.
        setCurrPage(0);
      } else {
        // Maintain pagination size by removing the last record if we're at capacity,
        // otherwise keep all existing records and prepend the new one.
        setExistingRecords((prev) => {
          const isAtCapacity = currPageSize >= PAGINATION_PAGE_SIZE;
          const rest = isAtCapacity ? prev.slice(0, -1) : prev;
          return [newRecord, ...rest];
        });
      }
    },
    [currPage, currPageSize, handleDeleteDraft, setCurrPage, setExistingRecords]
  );

  const handleSave = useCallback(async () => {
    // If the value is empty, don't create the record.
    if (!value.trim()) return;
    try {
      const response = await createKnowledgeHub({ type, content: value });
      handleApiResponse({
        response,
        errorMsg: 'Failed to create knowledge hub record. Please try again.',
        onSuccess: (data) => onCreateSuccess(data.record),
      });
    } catch (error) {
      console.error(error);
      showToast({ message: 'Something went wrong. Please try again.', type: AlertType.ERROR });
    }
  }, [type, value, createKnowledgeHub, handleApiResponse, onCreateSuccess, showToast]);

  return (
    <KnowledgeHubField
      isLoading={isCreateLoading}
      type={type}
      value={value}
      handleCancel={handleDeleteDraft}
      handleSave={handleSave}
      setValue={setValue}
    />
  );
};

export default CreateKnowledgeHubItem;
